import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoLogoWhite from "../../../img/eco/logo-white.png";
import { applyApi, getPublicCampaignApi, PublicError, type PublicCampaign } from "../campanhasApi";
import { POLITICA_PRIVACIDADE_PAGE } from "../routes";
import "./Candidatura.css";

const MAX_RESUME_MB = 5;
const RESUME_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";

const maskCpf = (v: string) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

type Phase = "loading" | "form" | "sent" | "closed" | "missing";

/**
 * Página de candidatura (sem login): o candidato chega pelo anúncio, preenche e
 * anexa o currículo. O anúncio de origem vem no `utm_content` do link.
 */
const CandidaturaPage: React.FC = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const adId = params.get("utm_content");
  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<PublicCampaign | null>(null);

  const [fullName, setFullName] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [resume, setResume] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getPublicCampaignApi(slug)
      .then((data) => {
        setInfo(data);
        setPhase(data.state === "ABERTA" ? "form" : "closed");
      })
      .catch((e) => setPhase(e instanceof PublicError && e.status === 404 ? "missing" : "closed"));
  }, [slug]);

  useEffect(() => {
    document.title = info ? `${info.jobTitle} · Candidatura` : "Candidatura";
  }, [info]);

  const pickResume = (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (file.size > MAX_RESUME_MB * 1024 * 1024) {
      setError(`O currículo passa de ${MAX_RESUME_MB} MB. Envie um arquivo menor.`);
      return;
    }
    setResume(file);
  };

  const missingRequired = useMemo(
    () => (info?.questions ?? []).filter((q) => q.required && !answers[q.id]?.trim()).map((q) => q.label),
    [info, answers],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info) return;
    setError(null);
    if (cpf.replace(/\D/g, "").length !== 11) return setError("Confira o CPF: são 11 números.");
    if (phone.replace(/\D/g, "").length < 10) return setError("Confira o celular, com o DDD.");
    if (missingRequired.length) return setError(`Responda: ${missingRequired[0]}`);
    if (info.resumeRequired && !resume) return setError("Anexe o seu currículo.");
    if (!consent) return setError("Para se candidatar, marque que concorda com o uso dos dados.");
    try {
      setSending(true);
      await applyApi(slug, { fullName, cpf, phone, email, city, neighborhood, answers, adId, website, resume });
      setPhase("sent");
      window.scrollTo({ top: 0 });
    } catch (err: any) {
      setError(err?.message || "Não foi possível enviar. Tente de novo.");
    } finally {
      setSending(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="candPage candPage--center">
        <p className="candHint">Carregando...</p>
      </div>
    );
  }

  if (phase !== "form" || !info) {
    const status =
      phase === "sent"
        ? { icon: "check" as const, title: "Candidatura enviada!", text: "Recebemos os seus dados. Se o seu perfil combinar com a vaga, entramos em contato pelo celular que você informou." }
        : phase === "missing"
          ? { icon: "x" as const, title: "Vaga não encontrada", text: "Confira o link. Se ele veio de um anúncio, a vaga pode ter sido retirada." }
          : info?.state === "ENCERRADA"
            ? { icon: "clock" as const, title: "Inscrições encerradas", text: "Esta vaga não recebe mais candidaturas. Obrigado pelo interesse!" }
            : { icon: "clock" as const, title: "Inscrições indisponíveis", text: "Esta vaga ainda não está recebendo candidaturas. Tente de novo mais tarde." };
    return (
      <div className="candPage">
        <header className="candHeader">
          <div className="candHeader__inner">
            <img className="candHeader__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
          </div>
        </header>
        <main className="candMain" style={{ alignItems: "center", paddingTop: 48 }}>
          <div className="candStatus" role="status">
            <span className="candStatus__icon" aria-hidden="true">
              <EcoIcon name={status.icon} size={30} />
            </span>
            <h1 className="candStatus__title">{status.title}</h1>
            <p className="candText" style={{ textAlign: "center" }}>
              {status.text}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="candPage">
      <header className="candHeader">
        <div className="candHeader__inner">
          <img className="candHeader__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
          <span className="candHeader__kicker">Vaga de freelancer</span>
          <h1 className="candHeader__title">{info.jobTitle}</h1>
          {info.locations.length ? (
            <div className="candChips">
              {info.locations.map((l) => (
                <span key={l} className="candChip">
                  <EcoIcon name="mapPin" size={14} /> {l}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </header>

      <main className="candMain">
        <p className="candText">{info.jobDescription}</p>

        <form className="candCard" onSubmit={submit} noValidate>
          <h2 className="candCard__title">Candidate-se</h2>

          <label className="candField">
            <span className="candLabel">Nome completo</span>
            <input className="candInput" autoComplete="name" required maxLength={160} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </label>

          <div className="candRow">
            <label className="candField">
              <span className="candLabel">CPF</span>
              <input className="candInput" inputMode="numeric" required value={cpf} onChange={(e) => setCpf(maskCpf(e.target.value))} placeholder="000.000.000-00" />
            </label>
            <label className="candField">
              <span className="candLabel">Celular (WhatsApp)</span>
              <input
                className="candInput"
                inputMode="tel"
                autoComplete="tel-national"
                required
                value={phone}
                onChange={(e) => setPhone(maskPhone(e.target.value))}
                placeholder="(21) 99999-8888"
              />
            </label>
          </div>

          <label className="candField">
            <span className="candLabel">
              E-mail <span className="candOptional">(opcional)</span>
            </span>
            <input className="candInput" type="email" autoComplete="email" maxLength={160} value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>

          <div className="candRow">
            <label className="candField">
              <span className="candLabel">Cidade</span>
              <input className="candInput" autoComplete="address-level2" required maxLength={120} value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className="candField">
              <span className="candLabel">Bairro</span>
              <input className="candInput" required maxLength={120} value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
            </label>
          </div>

          {info.questions.map((q) =>
            q.type === "SIM_NAO" ? (
              <fieldset key={q.id} className="candField">
                <legend className="candLabel">
                  {q.label} {q.required ? null : <span className="candOptional">(opcional)</span>}
                </legend>
                <div className="candChoices" style={{ marginTop: 6 }}>
                  {[
                    ["SIM", "Sim"],
                    ["NAO", "Não"],
                  ].map(([value, label]) => (
                    <label key={value} className="candChoice">
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        value={value}
                        checked={answers[q.id] === value}
                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: value }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : (
              <label key={q.id} className="candField">
                <span className="candLabel">
                  {q.label} {q.required ? null : <span className="candOptional">(opcional)</span>}
                </span>
                <textarea
                  className="candInput"
                  maxLength={500}
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                />
              </label>
            ),
          )}

          <div className="candField">
            <span className="candLabel">
              Currículo {info.resumeRequired ? null : <span className="candOptional">(opcional)</span>}
            </span>
            <div className="candFile">
              <label className="candSecondary">
                <EcoIcon name="file" size={18} />
                {resume ? "Trocar arquivo" : "Escolher arquivo"}
                <input type="file" accept={RESUME_ACCEPT} className="candTrap" onChange={(e) => pickResume(e.target.files?.[0])} />
              </label>
              <span className="candHint">{resume ? resume.name : `PDF, Word ou foto, até ${MAX_RESUME_MB} MB.`}</span>
            </div>
          </div>

          {/* Campo isca: pessoa não vê; robô preenche. */}
          <div className="candTrap" aria-hidden="true">
            <label>
              Site
              <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </label>
          </div>

          {info.consent.text ? (
            <label className="candConsent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                {info.consent.text}{" "}
                <Link className="candLink" to={POLITICA_PRIVACIDADE_PAGE()} target="_blank">
                  Ler a Política de Privacidade
                </Link>
              </span>
            </label>
          ) : null}

          {error ? (
            <p className="candError" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="candPrimary" disabled={sending}>
            {sending ? "Enviando..." : "Enviar candidatura"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default CandidaturaPage;
